import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Email as EmailIcon,
  Description as TemplateIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { EmailTemplate, EmailConversation, Candidate } from '../../types';
import airtableService from '../../services/airtable';
import BulkEmailCampaign from '../../components/BulkEmailCampaign';
import EmailComposer from '../../components/EmailComposer';
import { EmailComposerData } from '../../types';

const EmailCenter: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [emailConversations, setEmailConversations] = useState<EmailConversation[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [showTemplateEmailComposer, setShowTemplateEmailComposer] = useState(false);
  const [selectedTemplateForEmail, setSelectedTemplateForEmail] = useState<EmailTemplate | null>(null);


  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch email data and candidates
      const [
        emailTemplatesResponse,
        emailConversationsResponse,
        candidatesResponse
      ] = await Promise.all([
        airtableService.getEmailTemplates(),
        airtableService.getEmailConversations(),
        airtableService.getCandidates()
      ]);

      // Set email data
      if (emailTemplatesResponse.success) {
        setEmailTemplates(emailTemplatesResponse.data);
      }
      if (emailConversationsResponse.success) {
        setEmailConversations(emailConversationsResponse.data);
      }
      if (candidatesResponse.success) {
        setCandidates(candidatesResponse.data);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch email data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowEmailComposer(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setShowEmailComposer(true);
  };

  const handleComposeWithTemplate = (template: EmailTemplate) => {
    setSelectedTemplateForEmail(template);
    setShowTemplateEmailComposer(true);
  };

  const handleTemplateCreation = async (emailData: EmailComposerData): Promise<void> => {
    try {
      if (editingTemplate) {
        // Update existing template
        const templateData = {
          templateName: emailData.subject || editingTemplate.templateName,
          subjectLine: emailData.subject || editingTemplate.subjectLine,
          emailBody: emailData.body || editingTemplate.emailBody,
          templateCategory: editingTemplate.templateCategory,
          variablesUsed: extractVariables(emailData.body || editingTemplate.emailBody).split(',').filter(v => v.trim()),
          active: editingTemplate.active,
          createdBy: editingTemplate.createdBy,
          lastModified: new Date()
        };

        const response = await airtableService.updateEmailTemplate(editingTemplate.id, templateData);
        if (response.success) {
          await fetchData();
          setShowEmailComposer(false);
          setEditingTemplate(null);
          console.log('Template updated successfully');
        } else {
          throw new Error(response.error || 'Failed to update template');
        }
      } else {
        // Create a new template
        const templateData = {
          templateName: emailData.subject || 'New Template',
          subjectLine: emailData.subject,
          emailBody: emailData.body,
          templateCategory: 'General' as const,
          variablesUsed: extractVariables(emailData.body).split(',').filter(v => v.trim()),
          active: true,
          createdBy: 'User',
          lastModified: new Date()
        };

        const response = await airtableService.createEmailTemplate(templateData);
        if (response.success) {
          await fetchData();
          setShowEmailComposer(false);
          console.log('Template created successfully');
        } else {
          throw new Error(response.error || 'Failed to create template');
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setError(error instanceof Error ? error.message : 'Failed to save template');
    }
  };

  const extractVariables = (text: string): string => {
    const variables = text.match(/\{[^}]+\}/g) || [];
    return variables.map(v => v.replace(/[{}]/g, '')).join(',');
  };

  const handleTemplateEmailSend = async (emailData: EmailComposerData): Promise<void> => {
    try {
      // Import email service dynamically to avoid circular deps
      const emailService = (await import('../../services/emailService')).default;

      // Add emails to queue for sending
      const queueResponse = await emailService.addToQueue(emailData, 'current-user-id');
      if (queueResponse.success) {
        console.log('✅ Email queued successfully');
        setShowTemplateEmailComposer(false);
        setSelectedTemplateForEmail(null);
      } else {
        throw new Error(queueResponse.error || 'Failed to queue email');
      }
    } catch (error) {
      console.error('❌ Error sending template email:', error);
      setError(error instanceof Error ? error.message : 'Failed to send email');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <EmailIcon sx={{ color: '#f59e0b', fontSize: '2rem' }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #92400e 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Email Center
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={fetchData}
          sx={{
            borderColor: 'rgba(245, 158, 11, 0.5)',
            color: '#f59e0b',
            '&:hover': {
              borderColor: '#f59e0b',
              backgroundColor: 'rgba(245, 158, 11, 0.1)'
            }
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Email & Messaging Center */}
      <Card sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 50%, #581c87 100%)' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <EmailIcon sx={{ color: '#f59e0b', fontSize: '2rem', mr: 2 }} />
            <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
              Professional Email Management
            </Typography>
          </Box>

          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '& .MuiTab-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .Mui-selected': { color: '#f59e0b !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#f59e0b' }
            }}
          >
            <Tab label="Email Templates" />
            <Tab label="Bulk Email Campaigns" />
            <Tab label="Email Conversations" />
          </Tabs>

          {/* Email Templates Tab */}
          {selectedTab === 0 && (
            <Box sx={{ pt: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Professional Email Templates
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateTemplate}
                  sx={{
                    backgroundColor: '#f59e0b',
                    '&:hover': { backgroundColor: '#d97706' }
                  }}
                >
                  Create Template
                </Button>
              </Box>

              {emailTemplates.length > 0 ? (
                <Grid container spacing={2}>
                  {emailTemplates.map((template) => (
                    <Grid item xs={12} sm={6} md={3} key={template.id}>
                      <Card sx={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ color: '#f59e0b', mb: 1 }}>
                            {template.templateName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                            Subject: {template.subjectLine}
                          </Typography>
                          <Chip
                            label={template.templateCategory}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(245, 158, 11, 0.2)',
                              color: '#f59e0b',
                              mb: 2
                            }}
                          />
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              startIcon={<SendIcon />}
                              onClick={() => handleComposeWithTemplate(template)}
                              sx={{
                                color: '#10b981',
                                '&:hover': {
                                  backgroundColor: 'rgba(16, 185, 129, 0.1)'
                                }
                              }}
                            >
                              Compose Email
                            </Button>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditTemplate(template)}
                              sx={{ color: '#60a5fa' }}
                            >
                              Edit
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box textAlign="center" py={4}>
                  <TemplateIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    No email templates found. Create your first template to get started.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Bulk Email Campaigns Tab */}
          {selectedTab === 1 && (
            <Box sx={{ pt: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Bulk Email Campaigns
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setShowBulkEmailDialog(true)}
                  sx={{
                    backgroundColor: '#f59e0b',
                    '&:hover': { backgroundColor: '#d97706' }
                  }}
                >
                  Start Campaign
                </Button>
              </Box>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                Send professional emails to multiple candidates with SMTP protection and rate limiting.
              </Typography>
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                p: 3
              }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}>
                  📧 <strong>Safe Bulk Email Features:</strong>
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    • 45-second delays between emails prevent SMTP blocking
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    • Batch processing with 5-minute breaks
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    • Test mode for safe campaign validation
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    • Real-time progress monitoring
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    • Emergency stop functionality
                  </Typography>
                </Box>
              </Card>
            </Box>
          )}

          {/* Email Conversations Tab */}
          {selectedTab === 2 && (
            <Box sx={{ pt: 3 }}>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                Email Conversations
              </Typography>
              {emailConversations.length > 0 ? (
                <Grid container spacing={2}>
                  {emailConversations.map((conversation) => (
                    <Grid item xs={12} sm={6} md={3} key={conversation.id}>
                      <Card sx={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ color: '#f59e0b', mb: 1 }}>
                            {conversation.subject}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {conversation.candidate ? `${conversation.candidate.firstName} ${conversation.candidate.lastName} (${conversation.candidate.email})` : 'Unknown participant'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            {conversation.conversationThread?.length || 0} messages
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box textAlign="center" py={4}>
                  <EmailIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    No email conversations found.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Bulk Email Campaign Dialog */}
      <BulkEmailCampaign
        open={showBulkEmailDialog}
        onClose={() => setShowBulkEmailDialog(false)}
        candidates={candidates}
      />

      {/* Email Composer for Template Creation/Editing */}
      <EmailComposer
        open={showEmailComposer}
        onClose={() => {
          setShowEmailComposer(false);
          setEditingTemplate(null);
        }}
        recipients={[]} // Empty for template creation
        defaultTemplate={editingTemplate || undefined}
        onSend={handleTemplateCreation}
        onSave={handleTemplateCreation}
      />

      {/* Email Composer for Template-Based Emails with Recipient Selection */}
      <EmailComposer
        open={showTemplateEmailComposer}
        onClose={() => {
          setShowTemplateEmailComposer(false);
          setSelectedTemplateForEmail(null);
        }}
        recipients={candidates} // Pass all candidates so user can select from them
        defaultTemplate={selectedTemplateForEmail || undefined}
        onSend={handleTemplateEmailSend}
      />
    </Box>
  );
};

export default EmailCenter;